import { CommonModule } from '@angular/common'
import { Component, inject, OnInit, OnDestroy } from '@angular/core'
import { ActivatedRoute, RouterModule } from '@angular/router'
import { NotificationService } from '../services/notification.service'
import { Store } from '@ngrx/store'
import { selectUser } from '../store/auth/auth.state'
import { debounceTime, distinctUntilChanged, Observable, Subject, take, takeUntil } from 'rxjs'
import { Notification } from '../store/notification/notification.state'
import { selectOffices } from '../store/office/office.reducer'
import { UtilsService } from '../services/utils.service'
import { Office } from '../store/office/office.state'
import { ToastrService } from 'ngx-toastr'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { PaginationComponent } from '../components/template/pagination/pagination.component'
import dayjs from 'dayjs'
import { NgxDaterangepickerBootstrapDirective } from 'ngx-daterangepicker-bootstrap'
import { eps } from '../services/url.service'

interface DateRange {
  startDate: dayjs.Dayjs
  endDate: dayjs.Dayjs
}

@Component({
  selector: 'app-notification',
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    PaginationComponent,
    NgxDaterangepickerBootstrapDirective
  ],
  templateUrl: './notification.component.html',
  styleUrl: './notification.component.scss'
})
export class NotificationComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute)
  private notificationService = inject(NotificationService)
  private store = inject(Store)
  private toastr = inject(ToastrService)
  private destroy$ = new Subject<void>()
  private fetchTrigger$ = new Subject<void>()
  private searchSubject = new Subject<string>()
  notificationList: Notification[] = []
  totalCount = 0
  currentPage = 1
  pageLimit = 10
  search = ''
  eps = eps
  pageLastRecord = 0
  userId: string | null = null
  offices$!: Observable<Office[]>
  dateRange!: DateRange
  dateRangeModal = ''
  currentUserId: string | null = null

  constructor(
    private utilService: UtilsService
    // private router: Router
  ) {
    this.offices$ = this.store.select(selectOffices)
  }

  ngOnInit() {
    this.userId = this.route.snapshot.paramMap.get('id') //it use for path
    this.userId = this.route.snapshot.queryParamMap.get('id') //it use for queryparam
    this.searchSubject
      .pipe(debounceTime(1500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => {
        this.search = value
        this.currentPage = 1
        this.getNotificationsList()
      })
    if (!this.userId) {
      this.store
        .select(selectUser)
        .pipe(take(1))
        .subscribe(user => {
          this.userId = user?.id || null
          if (this.userId) {
            this.getNotificationsList()
          }
        })
    } else {
      this.getNotificationsList()
    }
    // this.router.navigate([eps.leaves.appliedLeavePath])
    this.store
      .select(selectUser)
      .pipe(take(1))
      .subscribe(user => {
        this.currentUserId = user?.id || null
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  getDashboardRoute(): string {
    return this.utilService.getDashboardRoute()
  }

  onSearchChange(value: string) {
    this.searchSubject.next(value)
  }

  onDateChange() {
    this.currentPage = 1
    this.getNotificationsList()
  }

  goTopPage(page: number) {
    this.currentPage = page
    this.getNotificationsList()
  }

  // getLeaveRoute(): { path: string; queryParams: { id: string } } | null {
  //   if (!this.selectedNotification?.moduleId) return null
  //   if (!this.selectedNotification?.appliedLeave) return null
  //   if (!this.currentUserId) return null
  //   const leaveId = this.selectedNotification.moduleId
  //   const userId = this.selectedNotification.appliedLeave.userId
  //   const path =
  //     userId === this.currentUserId
  //       ? this.eps.leaves.appliedLeavePath
  //       : this.eps.leaves.leavesRequestPath
  //   return {
  //     path,
  //     queryParams: { id: leaveId }
  //   }
  // }
  // getLeaveRoute(notification: Notification): { path: string; queryParams: { id: string } } | null {
  //   if (!notification?.moduleId) return null
  //   if (!notification?.appliedLeave) return null
  //   if (!this.currentUserId) return null
  //   const leaveId = notification.moduleId
  //   const userId = notification.appliedLeave.userId
  //   const path =
  //     userId === this.currentUserId
  //       ? this.eps.leaves.appliedLeavePath
  //       : this.eps.leaves.leavesRequestPath
  //   return {
  //     path,
  //     queryParams: { id: leaveId }
  //   }
  // }

  getLeaveRoute(notification: Notification): { path: string; queryParams: { id: string } } | null {
    if (!notification?.moduleId) return null
    if (!this.currentUserId) return null
    const leaveId = notification.moduleId
    if (!notification?.appliedLeave) {
      return {
        path: this.eps.user.employeeRequestPath,
        queryParams: { id: leaveId }
      }
    }
    const userId = notification.appliedLeave.userId
    const path =
      userId === this.currentUserId
        ? this.eps.leaves.appliedLeavePath
        : this.eps.leaves.leavesRequestPath
    return {
      path,
      queryParams: { id: leaveId }
    }
  }

  customSearchFnNotification = (term: string, item: Notification) => {
    if (!term) return true
    const lowerTerm = term.toLowerCase()
    const title = item.title?.toLowerCase() ?? ''
    const dateStr = item.createdAt
      ? new Date(item.createdAt).toLocaleDateString().toLowerCase()
      : ''
    return title.includes(lowerTerm) || dateStr.includes(lowerTerm)
  }

  selectedNotification: Notification | null = null

  openNotification(notification: Notification) {
    this.selectedNotification = notification
    this.notificationService.markAsRead(notification.id).subscribe({
      next: () => {
        notification.isRead = true
      },
      error: err => {
        this.toastr.error('Error marking as read: ' + err.message)
      }
    })
  }

  getNotificationsList() {
    if (!this.userId) return
    let fromDate: string | undefined
    let toDate: string | undefined
    if (this.dateRange?.startDate && this.dateRange?.endDate) {
      fromDate = this.dateRange.startDate.format('YYYY-MM-DD')
      toDate = this.dateRange.endDate.format('YYYY-MM-DD')
    }
    this.notificationService
      .list(this.userId!, this.search, this.currentPage, this.pageLimit, fromDate, toDate)
      .subscribe({
        next: res => {
          this.notificationList = res.notifications
          this.totalCount = res.totalCount
          this.pageLastRecord = Math.min(this.currentPage * this.pageLimit, this.totalCount)
        },
        error: err => {
          this.toastr.error('Error: ' + err.message)
        }
      })
  }
}
